import { find } from "lodash";
import { h } from "preact";
import {
  IWidgetConfigurationProps,
  withWidgetConfiguration,
} from "../../../node_modules/@sgwt-widget/core";
import { ApiBackedScComponent } from "../../common/components/ApiBackedScComponent";
import { YtdFiguresRow } from "../../common/components/YtdFiguresRow";
import {
  MakeGenericApiCall,
  MakeSalesCreditRoleProfile,
} from "../../common/generic/generic-api";
import {
  CheckReponseDataForError,
  GenericFilterBuilder,
} from "../../common/generic/generic-helper";

import { NbiDimensions } from "../../common/generic/generic-nbi-dimensions";
import { SCAttributes } from "../../common/generic/generic-sc-attributes";
import { IGenericRequest } from "../../common/generic/generic-types";
import {
  ConvertFilter,
  ValidateGenericData,
} from "../../common/salescredit/salescredit-filter-help";
import {
  BuildSalesCreditFilters,
  CreateDateFromFilter,
} from "../../common/salescredit/salescredit-search-criteria";
import { IProps } from "../../common/salescredit/salescredit-types";
import { maxSelectedClients } from "../../common/utils/clientlistHelper";
import { Format } from "../../common/utils/format";
import {
  GetKpiDetails,
  GetUsdKpiDetails,
  MapGenericData,
  MapGenericUsdData,
} from "../utils/helper";

export interface IState {
  data?: any;
}
const attr = new NbiDimensions();
const noPaddingOrNoMargin = "0px 0px 0px 0px";

export class SalesCreditOverviewClass extends ApiBackedScComponent<
  IProps & IWidgetConfigurationProps,
  IState
> {
  private isUsdUsers = false;

  private readonly SalesCreditStyles = {
    DirectionColor: {
      down: {
        color: "#b9292d",
      },
      up: {
        color: "#35bd5b",
      },
    },
  };

  private readonly PanelStyle = {
    Border: {
      outline: "#000000 solid 1px",
    },
    ExtraLeftPadding: {
      padding: noPaddingOrNoMargin,
      width: "50%",
    },
    MinPadding: {
      padding: "3px 0px 0px 0px",
    },
    NoMarginsPadding: {
      margin: noPaddingOrNoMargin,
      padding: noPaddingOrNoMargin,
      width: "100%",
    },
    NoPadding: {
      padding: "2px 5px 2px 2px",
    },
  };

  constructor() {
    super();
    this.setState({
      data: undefined,
    });
  }

  protected shouldFetchApiData(nextProps: IProps): boolean {
    this.isUsdUsers = this.isUsdScreenCheck(nextProps);
    return true;
  }

  // tslint:disable-next-line: no-big-function
  protected renderWithDataReady(): JSX.Element {
    return (
      <div class="container" style="width: 100%; height: 100%">
        <div class="row">
          <div class="container col-12" style="width: 100%">
            <YtdFiguresRow
              title="SC YTD"
              arrowDirection={Format.GetArrowClass(
                Number(this.state.data.YTD),
                Number(this.state.data.YTD_1)
              )}
              arrowColor={Format.GetArrowColor(
                Number(this.state.data.YTD),
                Number(this.state.data.YTD_1)
              )}
              value={Format.FormatMoney(this.state.data.YTD, 1000, 0) + " K"}
              unit={this.isUsdUsers}
              label={Format.GetLabelClass(
                Number(this.state.data.YTD),
                Number(this.state.data.YTD_1)
              )}
              labelValue={
                Format.CalculateVariation(
                  Number(this.state.data.YTD),
                  Number(this.state.data.YTD_1)
                ) + " % YoY"
              }
            />
            <YtdFiguresRow
              title="SC YTD-1"
              value={Format.FormatMoney(this.state.data.YTD_1, 1000, 0) + " K"}
              unit={this.isUsdUsers}
            />
            <YtdFiguresRow
              title="SC FY-1"
              value={Format.FormatMoney(this.state.data.FY_1, 1000, 0) + " K"}
              unit={this.isUsdUsers}
            />
            <YtdFiguresRow
              title="SC D-1"
              value={Format.FormatMoney(this.state.data.D_1, 1000, 0) + " K"}
              unit={this.isUsdUsers}
            />
            <YtdFiguresRow
              title="SC D-2"
              value={Format.FormatMoney(this.state.data.D_2, 1000, 0) + " K"}
              unit={this.isUsdUsers}
            />
            <YtdFiguresRow
              title="SC WTD"
              value={Format.FormatMoney(this.state.data.WTD, 1000, 0) + " K"}
              unit={this.isUsdUsers}
            />
            <YtdFiguresRow
              title="SC MTD"
              value={Format.FormatMoney(this.state.data.MTD, 1000, 0) + " K"}
              unit={this.isUsdUsers}
            />
          </div>
        </div>
      </div>
    );
  }

  protected async fetchApiData(props: IProps) {
    this.initCorrelationId();
    this.setDataStatus("loading");
    const userRole = await MakeSalesCreditRoleProfile(
      props.headers,
      this.props.widgetConfiguration,
      undefined,
      this.state.correlationId
    );
    if (userRole.Role === undefined) {
      this.setDataStatus("no-access-rights");
      this.setStatusText(userRole.StatusText);
      return;
    }

    const filtersConverted = ConvertFilter(props.filters);
    const filters = BuildSalesCreditFilters(filtersConverted);
    const date = CreateDateFromFilter(filtersConverted);

    const genericRequest: IGenericRequest = {
      date,
      filter: GenericFilterBuilder(filters),
      kpis: this.isUsdUsers
        ? GetUsdKpiDetails(userRole).join(",")
        : GetKpiDetails(userRole).join(","),
    };
    // removing sales name empty filter
    filters.pop();

    const filterClientsSelected = find(filters, {
      attribute: "clientgen_id",
    });
    const clientsCount = filterClientsSelected
      ? filterClientsSelected.value.split("+").length
      : 0;
    if (filterClientsSelected && clientsCount >= maxSelectedClients) {
      this.setState({
        data: [],
      });
      this.setDataStatus("too-many-clients");
    } else {
      const result = await MakeGenericApiCall(
        props.headers,
        "sc",
        genericRequest,
        this.props.widgetConfiguration,
        this.state.correlationId
      );
      const errorStatus = CheckReponseDataForError(result);
      if (errorStatus) {
        this.setDataStatus(errorStatus);
        this.setStatusText(result.statusText);
        this.setState({
          data: undefined,
        });
        return;
      }
      if (!ValidateGenericData(result)) {
        this.setState({
          data: undefined,
        });
        this.setDataStatus("no-data");
        return;
      }

      const mapData = this.isUsdUsers
        ? MapGenericUsdData(result, userRole)
        : MapGenericData(result, userRole);

      if (mapData !== undefined) {
        this.setState({
          data: mapData,
        });
        this.setDataStatus("ready");
      } else {
        this.setState({
          data: undefined,
        });
        this.setDataStatus("no-data");
      }
    }
  }

  private isUsdScreenCheck(nextProps: IProps): boolean {
    return (
      this.props.attribute === attr.SC_OVERVIEW_USD ||
      (nextProps !== undefined && nextProps.attribute === attr.SC_OVERVIEW_USD)
    );
  }
}

export const SalesCreditOverview = withWidgetConfiguration(
  SalesCreditOverviewClass
);
